<template>
  <div>
    <MFALogin
      v-if="mfaInfo"
      :info-mfa="mfaInfo"
      :login-data="form"
      :on-mfa-login-success="loginHandler"
    />
    <SigninTemplate
      v-else
      :primary="loadingColor"
      :over-primary="loadingColor"
      :show-back-button="true"
      :slide-animation="slideAnimationActive"
      :tablet-query="true"
      :gradient-background="gradientBackgroundActive"
      @on-back-click="redirectToUrl()"
    >
      <template #header>
        <div class="signin__infos" data-testid="wrapper">
          <div
            v-if="theme && !gradientBackgroundActive"
            class="signin__infos--partner"
          >
            <img v-if="theme.logo" :src="theme.logo" :alt="theme.brandName" />
            <Heading v-else :level="1" as="h1">
              {{ theme.brandName }}
            </Heading>
          </div>
          <SkeletonLoader v-else-if="themeLoading">
            <div class="signin__infos-loader" />
          </SkeletonLoader>
          <div v-else class="signin__infos-container">
            <BrandImage
              :image="EBrandImageLogos.WARREN_SECONDARY"
              :schema="EBrandImageSchemas.POSITIVE"
              :fixed-schema-color="true"
              width="120"
            />
            <div class="signin__infos-texts">
              <Heading :level="1">
                Investir toma tempo, mas não precisa ser o seu
              </Heading>
              <BaseText size="p4" :color="NebraskaColors.elementAlpha">
                Acesse a forma mais eficiente de investir. Para seu patrimônio e
                para sua vida.
              </BaseText>
            </div>
          </div>
        </div>
      </template>
      <template #default>
        <div class="signin__form">
          <FadeSlideTransition>
            <component :is="componentStep" />
          </FadeSlideTransition>
          <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
        </div>
      </template>
    </SigninTemplate>
    <MembershipTermsModal
      :is-open="isMembershipTermsModalOpen"
      :login-data="loginData"
      @confirm="handleAcceptTerms()"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { setNebraskaScheme } from '@/modules/common/services/theme';
import {
  isPartnerRunningStandalone,
  redirectToPartnerArea
} from '@/modules/common/helpers/partner';
import { redirectToUrl } from '@/modules/common/helpers/route/route';
import {
  trackLogin,
  trackPageView,
  trackSendEmailClick,
  trackSendPasswordClick
} from '@/modules/authentication/helpers/track-signin-events';
import {
  ICustomTheme,
  setCustomTheme,
  StorageKeys
} from '@/modules/common/services/local-storage';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  BrandImage,
  EBrandImageLogos,
  EBrandImageSchemas,
  Heading,
  SigninTemplate,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import FadeSlideTransition from '@/modules/common/components/FadeSlideTransition/FadeSlideTransition.vue';
import { SignInFormStepComponents } from './helpers/step-component';
import { SignInForm, SignInFormSteps } from './types';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';
import { NotificationTypes } from '@/modules/wallet-builder/src/types';
import {
  ILoginResponse,
  acceptTerms,
  getThemeByParam,
  login,
  verifyTermsAndMfa
} from '@/modules/common/services/http/account';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { CustomerModule } from '@/modules/common/store/CustomerModule';
import { ICustomer } from '@/types/models';
import {
  IMFAInfo,
  IMFALoginResponse
} from '@/modules/authentication/services/mfa';
import { MFALogin } from '@/modules/authentication/views/SecurityView/components/MFALogin';
import MembershipTermsModal from '@/modules/authentication/components/MembershipTermsModal/MembershipTermsModal.vue';
import { getProspectStatus } from '@/modules/onboarding/signup/services/api';
import { ECoexistenceStatus } from '@/modules/onboarding/signup/types';

const route = useRoute();
const router = useRouter();
const mfaInfo = ref<IMFAInfo | null>(null);
const theme = ref<ICustomTheme | null>(null);
const themeLoading = ref<boolean>(false);
const formStep = ref<SignInFormSteps>(SignInFormSteps.Email);
const loadingColor = ref<string | null>(NebraskaColors.elementOverImage);
const recaptchaToken = ref<string>('');
const lastRecaptchaAction = ref<RecaptchaActions>();
const recaptcha = ref<IRecaptcha | null>(null);
const isLoading = ref<boolean>(false);
const isMembershipTermsModalOpen = ref<boolean>(false);
const loginData = ref<ILoginResponse | IMFALoginResponse | null>(null);
const form = ref<SignInForm>({
  email: '',
  password: '',
  token: ''
});

const gradientBackgroundActive = computed(() => {
  return !(theme && theme.value?.type === 'BrandManaged');
});

const slideAnimationActive = computed(() => {
  return window.innerWidth > 1024;
});

async function handleRecaptcha(RecaptchaAction: RecaptchaActions) {
  isLoading.value = true;
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;

  try {
    lastRecaptchaAction.value = RecaptchaAction;
    await recaptchaEl.onChallenge(RecaptchaAction);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
    isLoading.value = false;
  }
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  form.value.token = recaptchaToken.value;
  if (
    lastRecaptchaAction.value === RecaptchaActions.SIGN_IN &&
    recaptchaToken.value
  ) {
    await submit();
  } else if (
    lastRecaptchaAction.value === RecaptchaActions.PROSPECT_STATUS &&
    recaptchaToken.value
  ) {
    await handleProspectStatus();
  }
}

function resetRecaptcha() {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;
  if (recaptchaEl) recaptchaEl.onReset();
}

function isMfaError(
  err: unknown
): err is { message: string; custom: IMFAInfo } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    'custom' in err &&
    err.message === 'error_login_auth_token_verify'
  );
}

async function submit() {
  try {
    const response = await login(form.value);
    loginData.value = response;
    loginHandler(response);
  } catch (err: unknown) {
    if (isMfaError(err)) {
      mfaInfo.value = err.custom;
    } else {
      resetRecaptcha();
      notify({
        group: 'wrn-notifications',
        text: 'E-mail ou senha inválidos, tente novamente',
        type: NotificationTypes.error
      });
    }
    isLoading.value = false;
  }
}
async function loginHandler(data: IMFALoginResponse | ILoginResponse) {
  loginData.value = data;
  const response = await verifyTermsAndMfa(data.accessToken);
  if (!response.termsAccepted) {
    isMembershipTermsModalOpen.value = true;
    return;
  }

  handleSignIn();
}

async function handleAcceptTerms() {
  loginData.value && (await acceptTerms(loginData.value.accessToken));
  isMembershipTermsModalOpen.value = false;
  await handleSignIn();
}

async function handleSignIn() {
  if (loginData.value) {
    localStorage.setItem(StorageKeys.AccessToken, loginData.value.accessToken);
    setLocalStorageData(loginData.value);
  }
  await FeatureFlagsModule.fetchFeatureFlags();
  trackLogin();
  if (loginData.value && loginData.value.user.clientType.b2bPartner) {
    partnerSignin(loginData.value.user);
  } else {
    clientSignin();
  }
}

async function handleProspectStatus() {
  try {
    const response = await getProspectStatus(
      form.value.email,
      recaptchaToken.value
    );
    if (response.status === ECoexistenceStatus.Prospect) {
      changeToPasswordCreationStep();
    } else {
      changeToPasswordSignInStep();
    }
  } catch (err) {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

function setLocalStorageData(data: ILoginResponse | IMFALoginResponse) {
  const localStorageData = {
    [StorageKeys.Brand]: JSON.stringify(data?.brand || null),
    [StorageKeys.CustomerId]: data.user._id!,
    [StorageKeys.IsBrandOwner]: String(data.isBrandOwner)
  };

  localStorage.setItem('warren.home.experience', 'true');
  Object.entries(localStorageData).forEach(([key, value]) =>
    localStorage.setItem(key, value)
  );
  if (data.user.clientType.whitelabel)
    localStorage.setItem(StorageKeys.WhitelabelTheme, 'whitelabel');
}

async function clientSignin() {
  await CustomerModule.fetchCustomer();
  const redirect = route.query.redirect as string;
  router.replace(redirect || '/home');
  isLoading.value = false;
}

function partnerSignin(user: ICustomer) {
  const localStorageFeatures = localStorage.getItem(StorageKeys.Features);
  if (localStorageFeatures) {
    const featuresArray = JSON.parse(localStorageFeatures);
    localStorage.setItem(
      StorageKeys.PartnerFeatures,
      JSON.stringify(featuresArray)
    );
  }

  localStorage.setItem(StorageKeys.Partner, JSON.stringify(user));
  localStorage.setItem(StorageKeys.IsPartnerAccountSession, 'true');

  if (!isPartnerRunningStandalone()) {
    router.push({ name: 'b2b-dash' });
  } else {
    redirectToPartnerArea(user._id);
  }
  isLoading.value = false;
}

async function handleEmailSubmit(email: string) {
  trackSendEmailClick();
  isLoading.value = true;
  form.value.email = email;
  await handleRecaptcha(RecaptchaActions.PROSPECT_STATUS);
}

async function handlePasswordSubmit(password: string) {
  trackSendPasswordClick();
  isLoading.value = true;
  form.value.password = password;
  await handleRecaptcha(RecaptchaActions.SIGN_IN);
}

const changeToEmailStep = () => {
  form.value.password = '';
  formStep.value = SignInFormSteps.Email;
};

const changeToPasswordSignInStep = () => {
  trackSendEmailClick();
  formStep.value = SignInFormSteps.PasswordSignIn;
};

const changeToPasswordCreationStep = () => {
  trackSendEmailClick();
  formStep.value = SignInFormSteps.PasswordCreation;
};

const componentStep = computed(() => {
  return SignInFormStepComponents[formStep.value];
});

async function setTheme(
  themeParam: string,
  paramType: 'domain' | 'email'
): Promise<void> {
  try {
    loadingColor.value = NebraskaColors.backgroundSecondary;
    themeLoading.value = true;
    const themeResponse = await getThemeByParam(paramType, themeParam);
    theme.value = themeResponse;
    if (!theme.value) return;
    setNebraskaScheme(theme.value);
    setCustomTheme(theme.value);
  } catch (err) {
    console.error(err);
  } finally {
    loadingColor.value = null;
    themeLoading.value = false;
  }
}

async function checkTheme(): Promise<void> {
  const storageTheme = localStorage.getItem(StorageKeys.Theme);
  if (storageTheme) {
    theme.value = JSON.parse(storageTheme);
    loadingColor.value = null;
    return;
  }

  const brand = route.query.brand;
  if (typeof brand === 'string') {
    await setTheme(brand, 'domain');
  } else {
    loadingColor.value = null;
  }
}

async function setFormPassword(password: string) {
  form.value.password = password;
}

onMounted(() => {
  trackPageView();
  checkTheme();
});

provide('isLoading', isLoading);
provide('handlePasswordSubmit', handlePasswordSubmit);
provide('handleEmailSubmit', handleEmailSubmit);
provide('changeToEmailStep', changeToEmailStep);
provide('setTheme', setTheme);
provide('form', form);
provide('setFormPassword', setFormPassword);
provide('handleRecaptchaSignIn', handleRecaptcha);
</script>
<style lang="less" scoped>
:deep(.signin) {
  overflow: hidden;
}

:not(.signin--gradient) > :deep(.signin-template) {
  background: @theme-primary;
}

:deep(.signin-container) {
  transition: none;
  justify-content: center;
}

:deep(.link) {
  color: @element-primary;
}

.signin__infos {
  color: @static-white;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 654px;
  background-size: cover !important;
  width: 100%;
  animation: fade-in 1s linear;

  &--partner {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 50%;
    }
  }

  .heading {
    color: @static-white !important;
    max-width: 360px;
    margin-top: @size-spacing-x900;
    margin-bottom: @size-spacing-x400;
    text-align: left;

    @media screen and (max-width: 1024px) {
      max-width: 468px;
    }
  }

  .base-text {
    max-width: 330px;
    text-align: left;
  }

  &-loader {
    height: 170px;
    width: 100%;
    max-width: 530px;
    border-radius: @size-radius-x100;

    @media @tabletDown {
      max-width: 232px;
      height: 64px;
    }
  }

  .skeleton-container {
    width: 100%;
    padding: 0 @size-spacing-x400;
    display: flex;
    justify-content: center;
  }

  &-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 468px;
    width: fit-content;
  }
}

.signin__form {
  width: 100%;
  max-width: 468px;

  form {
    display: flex;
    margin-top: @size-spacing-x600;
    flex-direction: column;
    gap: @size-spacing-x600;
  }

  .form-link {
    color: @element-secondary;
    text-decoration: underline;
  }

  .form-buttons {
    display: flex;
    justify-content: space-between;
    gap: @size-spacing-x600;

    button {
      width: 100%;
    }
  }
}

@media screen and (max-width: 768px) {
  .signin__form {
    max-width: 100vw;
    padding: @size-spacing-x600 @size-spacing-x500;
    height: 100%;
  }
  .signin__infos {
    height: 201px;
    &-texts {
      display: none;
    }
    &-container {
      align-items: center;
    }
  }

  :deep(.signin.signin--tablet .signin-template) {
    grid-template-rows: 201px 1fr !important;
  }
}
</style>
